<template>
  <div class="file-uploader__wrapper">
    <p class="file-uploader__text">Подання файлів</p>
    <div ref="dropzoneArea" class="file-uploader" @click="triggerFileInput">
      <div
        v-if="selectedFiles.length === 0"
        class="file-uploader__dropzone flex align-center justify-center"
      >
        <OpenFolderIcon />
      </div>
      <UploadFilesList
        v-else
        :files="selectedFiles"
        :file-preview-urls="filePreviewUrls"
        @remove-file="handleRemoveFile"
      />
    </div>
    <p class="file-uploader__text">* перетягніть або виберіть з пристрою</p>
    <input
      type="file"
      ref="fileInput"
      @change="handleFileInputChange"
      :multiple="maxFiles !== 1"
      :accept="acceptedFileTypes"
      style="display: none;"
    />
  </div>
</template>

<script>
import UploadFilesList from '../upload-files-list';
import { HttpClient } from '@/shared/lib/http-client';
import { Uuid } from '@/shared/lib/uuid';
import { fileUtils } from '@/shared/ui/file-upload';
import { OpenFolderIcon } from '@/shared/ui/icons';

const { generateUUIDv4 } = Uuid();

export default {
  props: {
    uploadUrl: {
      type: String,
      required: true,
    },
    urlIdPlaceholder: {
      type: String,
      default: '{itemId}',
    },
    filesParamName: {
      type: String,
      default: 'files',
    },
    autoProcessQueue: {
      type: Boolean,
      default: false,
    },
    maxFiles: {
      type: Number,
      default: null,
      validator: value => value === null || value > 0,
    },
    acceptedFileTypes: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      selectedFiles: [],
      filePreviewUrls: {},
      currentItemId: null,
      uploadingFiles: new Map(),
    };
  },
  mounted() {
    const dropzoneArea = this.$refs.dropzoneArea;
    if (dropzoneArea) {
      dropzoneArea.addEventListener('dragover', this.handleDragOver);
      dropzoneArea.addEventListener('dragleave', this.handleDragLeave);
      dropzoneArea.addEventListener('drop', this.handleDrop);
    }
  },
  beforeDestroy() {
    const dropzoneArea = this.$refs.dropzoneArea;
    if (dropzoneArea) {
      dropzoneArea.removeEventListener('dragover', this.handleDragOver);
      dropzoneArea.removeEventListener('dragleave', this.handleDragLeave);
      dropzoneArea.removeEventListener('drop', this.handleDrop);
    }

    this.selectedFiles.forEach(fileWithMeta => {
      const url = this.filePreviewUrls[fileWithMeta.upload.uuid];
      if (url) {
        this.revokeFilePreviewUrl(url);
      }
    });
    this.filePreviewUrls = {};

    this.uploadingFiles.forEach(controller => controller.abort());
    this.uploadingFiles.clear();
  },
  mixins: [ fileUtils ],
  components: { UploadFilesList, OpenFolderIcon },
  methods: {
    generateUUIDv4,
    handleDragOver(event) {
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'copy';
      this.$refs.dropzoneArea.classList.add('is-dragover');
    },
    handleDragLeave(event) {
      event.preventDefault();
      event.stopPropagation();
      this.$refs.dropzoneArea.classList.remove('is-dragover');
    },
    handleDrop(event) {
      event.preventDefault();
      event.stopPropagation();
      this.$refs.dropzoneArea.classList.remove('is-dragover');

      const files = Array.from(event.dataTransfer.files);
      this.addFilesToQueue(files);
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileInputChange(event) {
      const files = Array.from(event.target.files);
      event.target.value = '';
      this.addFilesToQueue(files);
    },
    addFilesToQueue(files) {
      files.forEach(file => {
        if (this.acceptedFileTypes && !this.isValidFileType(file, this.acceptedFileTypes)) {
          this.$emit('file-rejected', { file, reason: 'type_not_accepted' });
          return;
        }

        if (this.isDuplicate(file)) {
          return;
        }

        if (this.maxFiles !== null && this.selectedFiles.length >= this.maxFiles) {
          this.$emit('file-rejected', { file, reason: 'max_files_reached' });
          return;
        }

        const uniqueId = this.generateUUIDv4();
        const fileWithMeta = {
          originalFile: file,
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
          upload: { uuid: uniqueId, progress: 0, status: 'queued' },
          statusKey: 'queued',
        };

        this.selectedFiles.push(fileWithMeta);
        this.createPreviewUrl(fileWithMeta);
        this.$emit('file-added', fileWithMeta);
      });

      if (this.autoProcessQueue && this.selectedFiles.length > 0) {
        this.$nextTick(() => this.processQueue());
      }
    },
    isValidFileType(file, acceptedTypes) {
      const targetFile = file.originalFile || file;
      if (!acceptedTypes) return true;

      const types = acceptedTypes.split(',').map(t => t.trim().toLowerCase());
      const fileType = targetFile.type ? targetFile.type.toLowerCase() : '';
      const fileExtension = targetFile.name ? '.' + targetFile.name.split('.').pop().toLowerCase() : '';

      return types.some(type => {
        if (type.startsWith('.')) {
          return fileExtension === type;
        } else if (type.endsWith('/*')) {
          return fileType.startsWith(type.slice(0, -1));
        } else {
          return fileType === type;
        }
      });
    },
    handleRemoveFile(fileToRemove) {
      this.selectedFiles = this.selectedFiles.filter(f => f.upload.uuid !== fileToRemove.upload.uuid);
      this.revokePreviewUrl(fileToRemove);

      const abortController = this.uploadingFiles.get(fileToRemove.upload.uuid);
      if (abortController) {
        abortController.abort();
        this.uploadingFiles.delete(fileToRemove.upload.uuid);
      }
      this.$emit('file-removed', fileToRemove);
    },
    isDuplicate(file) {
      return this.selectedFiles.some(f =>
        f.originalFile.name === file.name &&
        f.originalFile.size === file.size &&
        f.originalFile.type === file.type
      );
    },
    createPreviewUrl(fileWithMeta) {
      if (this.isImageFile(fileWithMeta.originalFile)) {
        this.$set(this.filePreviewUrls, fileWithMeta.upload.uuid, this.createFilePreviewUrl(fileWithMeta.originalFile));
      }
    },
    revokePreviewUrl(fileWithMeta) {
      const url = this.filePreviewUrls[fileWithMeta.upload.uuid];
      if (url) {
        this.revokeFilePreviewUrl(url);
        this.$delete(this.filePreviewUrls, fileWithMeta.upload.uuid);
      }
    },
    updateFileStatus(fileWithMeta, statusKey, statusText, progress = undefined) {
      const index = this.selectedFiles.findIndex(f => f.upload.uuid === fileWithMeta.upload.uuid);
      if (index !== -1) {
        this.$set(this.selectedFiles[index].upload, 'status', statusText);
        this.$set(this.selectedFiles[index], 'statusKey', statusKey);
        if (progress !== undefined) {
          this.$set(this.selectedFiles[index].upload, 'progress', progress);
        }
      }
    },
    setUploadId(itemId) {
      this.currentItemId = itemId;
    },
    getUploadUrl(itemId) {
      if (itemId) {
        return this.uploadUrl.replace(this.urlIdPlaceholder, itemId);
      }
      return this.uploadUrl;
    },
    async processQueue(itemId = null) {
      if (itemId !== null) {
        this.setUploadId(itemId);
      }

      for (const file of this.selectedFiles) {
        if ((file.statusKey === 'queued' || file.statusKey === 'error') && !this.uploadingFiles.has(file.upload.uuid)) {
          await this.uploadFile(file);
        }
      }
      this.$emit('queue-complete', this.selectedFiles);
    },
    async uploadFile(fileWithMeta) {
      const currentUploadUrl = this.getUploadUrl(this.currentItemId);

      const abortController = new AbortController();
      this.uploadingFiles.set(fileWithMeta.upload.uuid, abortController);

      try {
        this.updateFileStatus(fileWithMeta, 'uploading', 'завантаження', 0);
        const response = await HttpClient.uploadFiles(
          currentUploadUrl,
          [fileWithMeta.originalFile],
          this.filesParamName,
          {},
          {
            onUploadProgress: (progressEvent) => {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              this.updateFileStatus(fileWithMeta, 'uploading', 'завантаження', progress);
            },
            signal: abortController.signal,
          }
        );

        this.updateFileStatus(fileWithMeta, 'success', 'успішно', 100);
        this.$emit('upload-success', { file: fileWithMeta, response });
      } catch (error) {
        if (error.name === 'AbortError') {
          this.updateFileStatus(fileWithMeta, 'cancelled', 'скасовано');
        } else {
          this.updateFileStatus(fileWithMeta, 'error', 'помилка');
          this.$emit('upload-error', { file: fileWithMeta, error: error });
        }
      } finally {
        this.uploadingFiles.delete(fileWithMeta.upload.uuid);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.file-uploader {
  min-height: 100px;
  font-family: inherit;
  border: 1px dashed #FB923C;
  border-radius: 10px;
  cursor: pointer;

  &__text {
    color: #71717A;
    font-size: 14px;
    font-weight: 500;
    margin: 8px 0;
  }

  &__dropzone {
    min-height: 100px;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &.is-dragover {
    border-color: #3B82F6;
    background-color: #EFF6FF;
  }
}
</style>
